import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, LockOpen } from "lucide-react";
import { resolveBaseUrl } from "@/lib/portalApi";
import SiteLink from "@/components/SiteLink";

/**
 * `/portal/auth/unlock-account?token=…` — the link in the account-locked email
 * (cp `services/email.py`, cp #778).
 *
 * Opening the page does NOTHING by itself: email link scanners open every link
 * in a message, so an unlock on page load would let a scanner unlock the
 * account. The token is sent only when the person clicks "Unlock my account",
 * and it goes to CP in a POST body, never a query string, so it stays out of
 * request logs (su #196).
 */
export const Route = createFileRoute("/portal_/auth/unlock-account")({
  component: UnlockAccountPage,
});

type UnlockState = "ready" | "unlocking" | "unlocked" | "failed";

function UnlockAccountPage(): JSX.Element {
  const token = new URLSearchParams(window.location.search).get("token");

  const [state, setState] = useState<UnlockState>(token ? "ready" : "failed");
  const [retryable, setRetryable] = useState(false);
  const [error, setError] = useState<string | null>(
    token
      ? null
      : "This unlock link is incomplete. Sign in again to get a new one.",
  );

  async function unlock(): Promise<void> {
    setState("unlocking");
    setRetryable(false);
    try {
      const res = await fetch(
        `${resolveBaseUrl()}/api/v1/auth/unlock-account`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        },
      );
      if (res.ok) {
        setState("unlocked");
        return;
      }
      const data = await res.json().catch(() => ({}));
      setError(data.detail || "We couldn't unlock your account.");
      setState("failed");
    } catch {
      setError("Could not reach the server. Please try again.");
      setRetryable(true);
      setState("failed");
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 sm:px-6">
        <SiteLink
          href="/"
          className="inline-flex items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition hover:border-slate-300"
          style={{ width: "fit-content" }}
        >
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            width={180}
            height={64}
            className="h-12"
            style={{ width: "auto" }}
          />
          <span className="rounded-full bg-brand-blue-light/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.3em] text-brand-blue-dark">
            Client portal
          </span>
        </SiteLink>

        <div className="mt-12 flex justify-center">
          <Card className="w-full max-w-md">
            <UnlockCardBody
              state={state}
              error={error}
              retryable={retryable}
              onUnlock={unlock}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

function UnlockCardBody({
  state,
  error,
  retryable,
  onUnlock,
}: {
  state: UnlockState;
  error: string | null;
  retryable: boolean;
  onUnlock: () => void;
}): JSX.Element {
  switch (state) {
    case "unlocked":
      return (
        <>
          <CardHeader className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
            <CardTitle className="mt-4">Account unlocked</CardTitle>
            <CardDescription>You can sign in again now.</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <SiteLink href="/portal/sign-in">
              <Button>Sign In</Button>
            </SiteLink>
          </CardContent>
        </>
      );
    case "failed":
      return (
        <>
          <CardHeader className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
            <CardTitle className="mt-4">Unlock failed</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center gap-2">
            {retryable ? <Button onClick={onUnlock}>Try again</Button> : null}
            <SiteLink href="/portal/sign-in">
              <Button variant="outline">Go to Sign In</Button>
            </SiteLink>
          </CardContent>
        </>
      );
    default:
      return (
        <>
          <CardHeader className="text-center">
            <LockOpen className="mx-auto h-12 w-12 text-muted-foreground" />
            <CardTitle className="mt-4">Unlock your account</CardTitle>
            <CardDescription>
              Your account was locked after too many failed sign-in attempts. If
              that was you, unlock it below.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={onUnlock} disabled={state === "unlocking"}>
              {state === "unlocking" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Unlock my account
            </Button>
          </CardContent>
        </>
      );
  }
}
